import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { Product } from '../../data/products';

export type PrintTemplate = 
  | 'product-label'      // Ürün etiketi (fiyat + barkod)
  | 'shelf-tag'          // Raf etiketi (küçük, kompakt)
  | 'shipping-label'     // Kargo etiketi (adres + barkod)
  | 'price-tag'          // Fiyat etiketi (büyük fiyat)
  | 'inventory-tag'      // Envanter etiketi (stok bilgili)
  | 'bulk-product';      // Toplu ürün yazdırma

export interface PrintTemplateConfig {
  name: string;
  description: string;
  width: number;
  height: number;
  icon: string;
}

export const PRINT_TEMPLATES: Record<PrintTemplate, PrintTemplateConfig> = {
  'product-label': {
    name: 'Ürün Etiketi',
    description: 'Ürün adı, fiyat ve barkod ile standart etiket',
    width: 70,
    height: 50,
    icon: '🏷️',
  },
  'shelf-tag': {
    name: 'Raf Etiketi',
    description: 'Küçük raf etiketi - ürün adı ve fiyat',
    width: 50,
    height: 30,
    icon: '📐',
  },
  'shipping-label': {
    name: 'Kargo Etiketi',
    description: 'Sipariş ve adres bilgileri ile kargo etiketi',
    width: 100,
    height: 70,
    icon: '📦',
  },
  'price-tag': {
    name: 'Fiyat Etiketi',
    description: 'Büyük fiyat gösterimi ile etiket',
    width: 60,
    height: 40,
    icon: '💰',
  },
  'inventory-tag': {
    name: 'Envanter Etiketi',
    description: 'Stok ve SKU bilgileri ile envanter etiketi',
    width: 80,
    height: 50,
    icon: '📋',
  },
  'bulk-product': {
    name: 'Toplu Yazdırma',
    description: 'A4 sayfasına çoklu ürün etiketi',
    width: 210,
    height: 297,
    icon: '📑',
  },
};

const brandRed: [number, number, number] = [227, 30, 36];
const darkGray: [number, number, number] = [51, 51, 51];
const lightGray: [number, number, number] = [153, 153, 153];

async function addQRToPDF(doc: jsPDF, data: string, x: number, y: number, size: number): Promise<void> {
  const qrDataUrl = await QRCode.toDataURL(data, {
    width: size * 2,
    margin: 1,
    color: { dark: '#000000', light: '#FFFFFF' },
    errorCorrectionLevel: 'M',
  });
  doc.addImage(qrDataUrl, 'PNG', x, y, size, size);
}

function addBarcodeToPDF(doc: jsPDF, data: string, x: number, y: number, width: number, height: number): void {
  const cleanData = data.replace(/[^a-zA-Z0-9]/g, '');
  const barWidth = Math.max(0.3, width / (cleanData.length * 2 + 6));
  let currentX = x + barWidth * 2;
  doc.setFillColor(0, 0, 0);
  doc.rect(currentX, y, barWidth, height, 'F');
  currentX += barWidth * 2;
  doc.rect(currentX, y, barWidth, height, 'F');
  currentX += barWidth;
  for (let i = 0; i < cleanData.length && currentX < x + width - barWidth * 4; i++) {
    const charCode = cleanData.charCodeAt(i);
    const pattern = charCode % 3;
    if (pattern === 0) { doc.rect(currentX, y, barWidth, height, 'F'); currentX += barWidth * 2; }
    else if (pattern === 1) { doc.rect(currentX, y, barWidth * 2, height, 'F'); currentX += barWidth * 3; }
    else { doc.rect(currentX, y, barWidth, height, 'F'); currentX += barWidth; doc.rect(currentX, y, barWidth, height, 'F'); currentX += barWidth * 2; }
  }
  doc.rect(currentX, y, barWidth, height, 'F');
  currentX += barWidth * 2;
  doc.rect(currentX, y, barWidth, height, 'F');
}

// Product Label Template
async function generateProductLabel(doc: jsPDF, product: Product, x: number, y: number): Promise<void> {
  const config = PRINT_TEMPLATES['product-label'];
  const w = config.width;
  const h = config.height;

  // Border
  doc.setDrawColor(200, 200, 200);
  doc.roundedRect(x, y, w, h, 2, 2, 'S');

  // Red top bar
  doc.setFillColor(...brandRed);
  doc.rect(x, y, w, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.text('T-RaX', x + 3, y + 5.5);

  // Product name
  doc.setTextColor(...darkGray);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  let name = product.name;
  while (doc.getTextWidth(name) > w - 6 && name.length > 5) name = name.slice(0, -4) + '...';
  doc.text(name, x + 3, y + 15);

  // Price
  doc.setTextColor(...brandRed);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`₺${product.salePrice.toLocaleString('tr-TR')}`, x + 3, y + 25);

  if (product.salePrice < product.regularPrice) {
    doc.setTextColor(...lightGray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`₺${product.regularPrice.toLocaleString('tr-TR')}`, x + 3, y + 30);
    const priceWidth = doc.getTextWidth(`₺${product.regularPrice.toLocaleString('tr-TR')}`);
    doc.setDrawColor(...lightGray);
    doc.line(x + 3, y + 29, x + 3 + priceWidth, y + 29);
  }

  // QR Code
  await addQRToPDF(doc, `TRX-${product.id}`, x + w - 22, y + 10, 18);
}

// Shelf Tag Template
async function generateShelfTag(doc: jsPDF, product: Product, x: number, y: number): Promise<void> {
  const config = PRINT_TEMPLATES['shelf-tag'];
  const w = config.width;
  const h = config.height;

  doc.setDrawColor(200, 200, 200);
  doc.roundedRect(x, y, w, h, 1, 1, 'S');

  doc.setTextColor(...darkGray);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  let name = product.name;
  while (doc.getTextWidth(name) > w - 4 && name.length > 5) name = name.slice(0, -4) + '...';
  doc.text(name, x + 2, y + 8);

  doc.setTextColor(...brandRed);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`₺${product.salePrice.toLocaleString('tr-TR')}`, x + 2, y + 20);

  doc.setTextColor(...lightGray);
  doc.setFontSize(5);
  doc.text(`SKU: ${product.id}`, x + 2, y + 26);
}

// Price Tag Template (Large)
async function generatePriceTag(doc: jsPDF, product: Product, x: number, y: number): Promise<void> {
  const config = PRINT_TEMPLATES['price-tag'];
  const w = config.width;
  const h = config.height;

  doc.setFillColor(...brandRed);
  doc.roundedRect(x, y, w, h, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  let name = product.name;
  while (doc.getTextWidth(name) > w - 6 && name.length > 5) name = name.slice(0, -4) + '...';
  doc.text(name, x + 3, y + 10);

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(`₺${product.salePrice.toLocaleString('tr-TR')}`, x + 3, y + 25);

  if (product.salePrice < product.regularPrice) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`₺${product.regularPrice.toLocaleString('tr-TR')}`, x + 3, y + 32);
    const priceWidth = doc.getTextWidth(`₺${product.regularPrice.toLocaleString('tr-TR')}`);
    doc.setDrawColor(255, 255, 255);
    doc.line(x + 3, y + 31, x + 3 + priceWidth, y + 31);
  }
}

// Inventory Tag Template
async function generateInventoryTag(doc: jsPDF, product: Product, x: number, y: number): Promise<void> {
  const config = PRINT_TEMPLATES['inventory-tag'];
  const w = config.width;
  const h = config.height;

  doc.setDrawColor(200, 200, 200);
  doc.roundedRect(x, y, w, h, 2, 2, 'S');

  // Header
  doc.setFillColor(240, 240, 240);
  doc.rect(x, y, w, 10, 'F');
  doc.setTextColor(...darkGray);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('ENVANTER ETİKETİ', x + 3, y + 7);

  // Product info
  doc.setTextColor(...darkGray);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  let name = product.name;
  while (doc.getTextWidth(name) > w - 6 && name.length > 5) name = name.slice(0, -4) + '...';
  doc.text(name, x + 3, y + 18);

  doc.setFont('helvetica', 'bold');
  doc.text(`SKU: ${product.id}`, x + 3, y + 25);
  doc.text(`Stok: ${product.stock}`, x + 3, y + 31);

  doc.setTextColor(...brandRed);
  doc.setFontSize(10);
  doc.text(`₺${product.salePrice.toLocaleString('tr-TR')}`, x + 3, y + 40);

  // Barcode
  addBarcodeToPDF(doc, `TRX-${product.id}`, x + w - 45, y + 15, 40, 12);
}

// Shipping Label Template
async function generateShippingLabel(
  doc: jsPDF,
  orderData: { orderNumber: string; customerName: string; address: string; city: string; district: string; phone: string },
  x: number,
  y: number
): Promise<void> {
  const config = PRINT_TEMPLATES['shipping-label'];
  const w = config.width;
  const h = config.height;

  doc.setDrawColor(0, 0, 0);
  doc.roundedRect(x, y, w, h, 2, 2, 'S');

  // Header
  doc.setFillColor(...brandRed);
  doc.rect(x, y, w, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('T-RaX Car Care', x + 3, y + 8);

  doc.setTextColor(...darkGray);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(`Sipariş: ${orderData.orderNumber}`, x + 3, y + 20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text(`Alıcı: ${orderData.customerName}`, x + 3, y + 28);
  doc.text(`Tel: ${orderData.phone}`, x + 3, y + 34);

  // Address box
  doc.setDrawColor(200, 200, 200);
  doc.roundedRect(x + 2, y + 38, w - 4, 18, 1, 1, 'S');
  doc.setTextColor(...darkGray);
  doc.setFontSize(6);
  doc.text(orderData.address, x + 5, y + 45);
  doc.text(`${orderData.district}/${orderData.city}`, x + 5, y + 51);

  // QR Code
  await addQRToPDF(doc, orderData.orderNumber, x + w - 28, y + 38, 24);
}

// Bulk Product Labels (A4 page)
async function generateBulkProductLabels(doc: jsPDF, products: Product[]): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const labelConfig = PRINT_TEMPLATES['product-label'];
  const labelW = labelConfig.width;
  const labelH = labelConfig.height;
  const gapX = 5;
  const gapY = 5;

  const cols = Math.floor((pageWidth - margin * 2 + gapX) / (labelW + gapX));
  const rows = Math.floor((pageHeight - margin * 2 + gapY) / (labelH + gapY));
  const perPage = cols * rows;

  // Header
  doc.setFillColor(...brandRed);
  doc.rect(0, 0, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('T-RaX Car Care - Toplu Etiket Çıktısı', margin, 10);
  doc.setFontSize(7);
  doc.text(`${products.length} ürün | ${new Date().toLocaleDateString('tr-TR')}`, pageWidth - margin, 10, { align: 'right' });

  let startY = 20;

  for (let i = 0; i < products.length; i++) {
    const pageindex = Math.floor(i / perPage);
    const posOnPage = i % perPage;
    const col = posOnPage % cols;
    const row = Math.floor(posOnPage / cols);

    if (posOnPage === 0 && i > 0) {
      doc.addPage();
      // Repeat header on new page
      doc.setFillColor(...brandRed);
      doc.rect(0, 0, pageWidth, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('T-RaX Car Care - Toplu Etiket Çıktısı', margin, 10);
      startY = 20;
    }

    const x = margin + col * (labelW + gapX);
    const y = startY + row * (labelH + gapY);

    await generateProductLabel(doc, products[i], x, y);
  }
}

// Main generation function
export async function generatePrintTemplate(
  template: PrintTemplate,
  data: {
    products?: Product[];
    product?: Product;
    order?: { orderNumber: string; customerName: string; address: string; city: string; district: string; phone: string };
  }
): Promise<Blob> {
  const doc = new jsPDF('p', 'mm', 'a4');

  switch (template) {
    case 'product-label':
      if (data.product) await generateProductLabel(doc, data.product, 15, 15);
      break;
    case 'shelf-tag':
      if (data.product) await generateShelfTag(doc, data.product, 15, 15);
      break;
    case 'price-tag':
      if (data.product) await generatePriceTag(doc, data.product, 15, 15);
      break;
    case 'inventory-tag':
      if (data.product) await generateInventoryTag(doc, data.product, 15, 15);
      break;
    case 'shipping-label':
      if (data.order) await generateShippingLabel(doc, data.order, 15, 15);
      break;
    case 'bulk-product':
      if (data.products) await generateBulkProductLabels(doc, data.products);
      break;
  }

  return doc.output('blob');
}

export async function downloadPrintTemplate(
  template: PrintTemplate,
  data: {
    products?: Product[];
    product?: Product;
    order?: { orderNumber: string; customerName: string; address: string; city: string; district: string; phone: string };
  },
  filename?: string
): Promise<void> {
  const blob = await generatePrintTemplate(template, data);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `${PRINT_TEMPLATES[template].name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
